import { getFilterDefinition } from '../../engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  type FreeTextFilter,
  type ListFilter,
  type MultiFilter,
  type RangeFilter,
  type SchoolFilter,
  type StaticListFilter,
  type TreeFilter,
} from '../../types/filters';

/**
 * CustomDash represents "multiple chips for the same logical filter" via a MULTI_* wrapper
 * filter. This file centralizes that wrapping logic.
 *
 * Example:
 * - Role filter instances: [ {Role: Engineer}, {Role: Scientist} ]
 * - Output: MULTI_ROLE { instances: [...] }
 */
export const FILTER_TO_MULTI_FILTER_MAP: Partial<Record<FilterId, FilterId>> = {
  [FilterId.COMPANY]: FilterId.MULTI_COMPANY,
  [FilterId.GEOGRAPHY]: FilterId.MULTI_GEOGRAPHY,
  [FilterId.ROLE]: FilterId.MULTI_ROLE,
  [FilterId.SENIORITY]: FilterId.MULTI_SENIORITY,
  [FilterId.PAY]: FilterId.MULTI_PAY,
  [FilterId.FLIGHT_RISK]: FilterId.MULTI_FLIGHT_RISK,
  [FilterId.PRESTIGE]: FilterId.MULTI_PRESTIGE,
  [FilterId.REMOTE_SUITABILITY]: FilterId.MULTI_REMOTE_SUITABILITY,
  [FilterId.EMPLOYMENT_RELATIONSHIP]: FilterId.MULTI_EMPLOYMENT_RELATIONSHIP,
  [FilterId.WORK_SCHEDULE]: FilterId.MULTI_WORK_SCHEDULE,
  [FilterId.KEYWORDS]: FilterId.MULTI_KEYWORDS,
  [FilterId.TITLE]: FilterId.MULTI_TITLE,
  [FilterId.GENDER]: FilterId.MULTI_GENDER,
  [FilterId.ETHNICITY]: FilterId.MULTI_ETHNICITY,
  [FilterId.EDUCATION]: FilterId.MULTI_EDUCATION,
  [FilterId.GRADUATION_YEAR]: FilterId.MULTI_GRADUATION_YEAR,
  [FilterId.YEARS_OF_EXPERIENCE]: FilterId.MULTI_YEARS_OF_EXPERIENCE,
  [FilterId.SCHOOL]: FilterId.MULTI_SCHOOL,
  [FilterId.CUSTOM_SKILL]: FilterId.MULTI_CUSTOM_SKILL,
};

const isWrappableFilter = (
  filter: Filter
): filter is
  | TreeFilter
  | ListFilter
  | StaticListFilter
  | FreeTextFilter
  | RangeFilter
  | SchoolFilter => {
  return (
    filter.type === FilterType.TREE ||
    filter.type === FilterType.LIST ||
    filter.type === FilterType.STATIC_LIST ||
    filter.type === FilterType.FREE_TEXT ||
    filter.type === FilterType.RANGE ||
    filter.type === FilterType.SCHOOL
  );
};

const createNewMultiFilter = <
  T extends
    | TreeFilter
    | ListFilter
    | StaticListFilter
    | FreeTextFilter
    | RangeFilter
    | SchoolFilter,
>(
  filter: T,
  multiFilterId: FilterId,
  name?: string
): MultiFilter<T> => ({
  id: multiFilterId,
  name: name ?? filter.name,
  type: FilterType.MULTI,
  wrappedFilterId: filter.id,
  instances: [filter],
  isVisible: true,
  isUserSubmitted: true,
});

export const wrapFilterInMultiIfNeeded = (filter: Filter): Filter => {
  // Some filters are always displayed/managed as MULTI_* in the UI. If we got a single instance,
  // we still wrap it so the rest of the app can treat it uniformly.
  if (filter.type === FilterType.MULTI) {
    return filter;
  }

  const multiFilterId = FILTER_TO_MULTI_FILTER_MAP[filter.id];
  if (!multiFilterId) {
    return filter;
  }

  const definition = getFilterDefinition(multiFilterId);
  if (!isWrappableFilter(filter)) {
    return filter;
  }

  return createNewMultiFilter(
    filter,
    multiFilterId,
    definition?.name
  ) as Filter;
};

export const toMultiOrSingle = <T extends Filter>(
  baseFilterId: FilterId,
  instances: T[]
): Filter[] => {
  // Convert the accumulated instances for a base filter into the final Filter union.
  if (instances.length === 0) return [];

  const multiFilterId = FILTER_TO_MULTI_FILTER_MAP[baseFilterId];
  if (!multiFilterId) {
    return instances[0] ? [instances[0] as Filter] : [];
  }

  if (instances.length === 1) {
    const first = instances[0];
    return first ? [wrapFilterInMultiIfNeeded(first as Filter)] : [];
  }

  const definition = getFilterDefinition(multiFilterId);
  const multiFilter: MultiFilter<T> = {
    id: multiFilterId,
    name: definition?.name ?? String(multiFilterId),
    type: FilterType.MULTI,
    wrappedFilterId: baseFilterId,
    instances,
    isVisible: true,
    isUserSubmitted: true,
  };

  // The `Filter` union is intentionally explicit (MultiFilter<TreeFilter> | MultiFilter<ListFilter> | ...),
  // and TS can't prove `MultiFilter<T>` is always one of those here. The runtime shape is correct.
  return [multiFilter as unknown as Filter];
};
